import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuditTrail } from '../../../services/auditTrailApi';
import type { AuditTrailItem } from './auditTrailTypes';

const SESSION_EVENTS_LIMIT = 100;

/** Load every event for one login session (chronological). */
export function useAuditTrailSessionEvents(
  projectId: number | undefined,
  sessionId: string | null | undefined
) {
  const [events, setEvents] = useState<AuditTrailItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const canLoad = Boolean(sessionId && sessionId !== '—');

  const load = useCallback(async () => {
    if (!canLoad || !sessionId) {
      setEvents([]);
      setError(null);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const params = {
      session_id: sessionId,
      limit: SESSION_EVENTS_LIMIT,
      offset: 0,
      sortBy: 'created_at' as const,
      sortOrder: 'ASC' as const,
      signal: controller.signal,
      ...(projectId != null && Number.isFinite(projectId) ? { project_id: projectId } : {}),
    };

    try {
      const result = await fetchAuditTrail(params);

      if (controller.signal.aborted) return;

      if (!result.ok) {
        if (result.error === 'Request cancelled') return;
        setEvents([]);
        setError(result.error);
        return;
      }

      setEvents(result.data.items);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [canLoad, sessionId, projectId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { events, loading, error, reload: load };
}
